import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { Typography } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { httpClient } from '@web/services/singletons';
import { withCdnEntryFile } from '@web/vin-sessions/utils/open-document-tab';

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 16,
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 16,
        padding: 40,
        textAlign: 'center',
    },
});

export const SsmRedirectHandler: FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const { token } = useAuthState();
    const [error, setError] = useState<string | null>(null);
    const hasRedirected = useRef(false);

    useEffect(() => {
        if (hasRedirected.current) {
            return;
        }
        hasRedirected.current = true;

        const performRedirect = async () => {
            try {
                const storedPath = sessionStorage.getItem('ssm_redirect_path');
                sessionStorage.removeItem('ssm_redirect_path');

                // Path shape: /stis/diag/<modelYear>/<carlineCode>/<...deep path...>
                //   e.g. /stis/diag/2024/Impreza/contents/index.html
                // The backend resolves the CDN folder from modelYear + carlineCode (the deep path
                // would trip the WAF), so we send only those two params and re-append the remaining
                // sub-path to the returned documentUrl (same pattern as the htmlDiagnostics bundle).
                const source = storedPath ?? location.pathname + location.search;
                const afterDiag = source.replace(/^\/stis\/diag\//, '');
                const segments = afterDiag.split('/');
                const modelYear = segments[0];
                const carlineCode = segments[1];
                const remainder = segments.slice(2).join('/');

                if (!modelYear || !carlineCode) {
                    setError('Invalid redirect path. No diagnostic path was provided.');
                    return;
                }

                const res = await fetch(
                    `${httpClient.vinUrl}/docs/cdn-url-by-params?modelYear=${encodeURIComponent(
                        modelYear
                    )}&carlineCode=${encodeURIComponent(carlineCode)}`,
                    {
                        headers: {
                            'X-Subcomm-Access-Token': token,
                        },
                        credentials: 'include',
                    }
                );

                if (!res.ok) {
                    setError('The service manual document could not be found for the given path.');
                    return;
                }

                const data = await res.json();

                if (data?.documentUrl) {
                    // Append the remaining sub-path; if the path has no index.html file, default the
                    // bundle entry to index.html (withCdnEntryFile), per the SSM redirect spec.
                    const finalUrl = remainder
                        ? `${data.documentUrl.replace(/\/$/, '')}/${remainder}`
                        : data.documentUrl;
                    window.location.href = withCdnEntryFile(finalUrl) ?? finalUrl;
                } else {
                    setError('The service manual document could not be found for the given path.');
                }
            } catch (err) {
                logger.error('Failed to perform the SSM redirect', { error: err });
                setError('An error occurred while looking up the service manual document. Please try again later.');
            }
        };

        performRedirect();
    }, [location]);

    if (error) {
        return (
            <div className={classes.errorContainer}>
                <Typography variant="h3">{error}</Typography>
                <Typography variant="labelRegular" color="blueOne" onPress={() => navigate('/', { replace: true })}>
                    Go to Home
                </Typography>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Typography variant="h3">Redirecting to Service Manual...</Typography>
            <Typography variant="labelRegular">
                Please wait while we look up the document. You will be redirected to the Service Manual shortly.
            </Typography>
        </div>
    );
};
